import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemRoundedCheckbox } from "../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import type { OtMode } from "../../utils/models";

type Props = {
	laborCostEnabled?: boolean;
	onLaborCostToggle?: (enabled: boolean) => void;
	includeHolidays?: boolean;
	onIncludeHolidaysToggle?: (enabled: boolean) => void;
	includeLateScheduled?: boolean;
	onIncludeLateScheduledToggle?: (enabled: boolean) => void;
	/** Late-scheduled only applies to the scheduled view, so the row hides when viewing actuals. */
	isActual?: boolean;
	// Optional here (unlike the header prop) so the header can wage-gate it — passing `undefined`
	// hides the row for users who can't view wages, the same way Labor Cost is gated.
	estimateMissingWage?: boolean;
	onEstimateMissingWageToggle?: (enabled: boolean) => void;
	otMode: OtMode;
	onOtModeChange: (mode: OtMode) => void;
	onAlignToDayView: () => void;
	canSaveAsDefault: boolean;
	onSaveAsDefault: () => void | Promise<void>;
	onRestore: () => void | Promise<void>;
};

const InfoIcon: FC<{ title: ReactNode }> = ({ title }) => (
	<InHouseTooltip title={title} arrow placement="top">
		<InfoOutlinedIcon sx={{ fontSize: "16px", color: "text.secondary" }} />
	</InHouseTooltip>
);

const Row: FC<{ children: ReactNode }> = ({ children }) => (
	<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>{children}</Box>
);

export const ManagementCenterSettingsMenu: FC<Props> = ({
	laborCostEnabled,
	onLaborCostToggle,
	includeHolidays,
	onIncludeHolidaysToggle,
	includeLateScheduled,
	onIncludeLateScheduledToggle,
	isActual,
	estimateMissingWage,
	onEstimateMissingWageToggle,
	otMode,
	onOtModeChange,
	onAlignToDayView,
	canSaveAsDefault,
	onSaveAsDefault,
	onRestore,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [showSaveConfirm, setShowSaveConfirm] = useState(false);

	const otTooltip = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
			<span>{t("ot.mode.trigger.tooltip")}</span>
			<span>{t("ot.mode.actual.tooltip")}</span>
		</Box>
	);

	return (
		<>
			<SystemReactTinyPopover
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				onClickOutside={() => setIsOpen(false)}
				positions={["bottom", "top"]}
				align="end"
				content={
					<Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25, minWidth: 260 }} data-testid="management-center-settings-menu">
						{onLaborCostToggle && (
							<Row>
								<SystemRoundedCheckbox
									checked={laborCostEnabled ?? false}
									onChange={onLaborCostToggle}
									label={t("labor.cost")}
									data-testid="mc-setting-labor-cost"
								/>
								<InfoIcon title={t("labor.cost.tooltip")} />
							</Row>
						)}
						{onIncludeHolidaysToggle && (
							<Row>
								<SystemRoundedCheckbox
									checked={includeHolidays ?? false}
									onChange={onIncludeHolidaysToggle}
									label={t("include.holidays")}
									data-testid="mc-setting-include-holidays"
								/>
								<InfoIcon title={t("include.holidays.tooltip")} />
							</Row>
						)}
						{onIncludeLateScheduledToggle && !isActual && (
							<Row>
								<SystemRoundedCheckbox
									checked={includeLateScheduled ?? false}
									onChange={onIncludeLateScheduledToggle}
									label={t("include.late.scheduled")}
									data-testid="mc-setting-include-late-scheduled"
								/>
								<InfoIcon title={t("include.late.scheduled.tooltip")} />
							</Row>
						)}
						{onEstimateMissingWageToggle && (
							<Row>
								<SystemRoundedCheckbox
									// DEFAULT: on, matching the `mc_estimate_missing_wage` server default (APP-731), so the
									// box reflects the estimate the cost figures are actually using while settings load.
									checked={estimateMissingWage ?? true}
									onChange={onEstimateMissingWageToggle}
									label={t("estimate.missing.wage")}
									data-testid="mc-setting-estimate-missing-wage"
								/>
								<InfoIcon title={t("estimate.missing.wage.tooltip")} />
							</Row>
						)}
						<Row>
							{/* FE-only, temporary: always on (gross hours, without meal break). */}
							<SystemRoundedCheckbox checked disabled onChange={() => {}} label={t("exclude.meal.break")} data-testid="mc-setting-exclude-meal-break" />
							<InfoIcon title={t("exclude.meal.break.tooltip")} />
						</Row>
						<Row>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Typography sx={{ fontSize: "14px", fontWeight: 500, color: "primary.dark" }}>{t("ot.mode")}</Typography>
								<InfoIcon title={otTooltip} />
							</Box>
							<SystemFlatSwitch
								options={[
									{ label: t("ot.mode.trigger"), value: "trigger" as OtMode },
									{ label: t("ot.mode.actual"), value: "actual" as OtMode },
								]}
								activeIndexOrValue={otMode}
								onSwitch={(option) => onOtModeChange(option.value as OtMode)}
							/>
						</Row>

						<SystemButton
							sx={{
								width: { xs: "100%", sm: "100%" },
							}}
							buttonVariant="primary"
							size="small"
							onClick={onAlignToDayView}
							data-testid="mc-settings-align-to-day-view"
						>
							{t("align.to.day.view")}
						</SystemButton>
						<Box sx={{ display: "flex", gap: 1, mt: 1.25 }}>
							<SystemButton buttonVariant="borderless" size="small" onClick={() => onRestore()} sx={{ flex: 1 }} data-testid="mc-settings-restore">
								{t("settings.restore")}
							</SystemButton>
							{canSaveAsDefault && (
								<SystemButton
									buttonVariant="secondary"
									size="small"
									onClick={() => setShowSaveConfirm(true)}
									sx={{ flex: 1 }}
									data-testid="mc-settings-save-default"
								>
									{t("settings.save.as.default")}
								</SystemButton>
							)}
						</Box>
					</Box>
				}
			>
				<Tooltip title={t("management.center.settings")}>
					<IconButton
						onClick={() => setIsOpen((open) => !open)}
						size="small"
						aria-label={t("management.center.settings")}
						data-testid="management-center-settings-toggle"
					>
						<SettingsOutlinedIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</SystemReactTinyPopover>
			{canSaveAsDefault && (
				<SystemDialog
					dataTestId="mc-settings-save-default-dialog"
					open={showSaveConfirm}
					onClose={() => setShowSaveConfirm(false)}
					title={t("settings.save.as.default.confirm.title")}
					body={t("settings.save.as.default.confirm.body")}
					onConfirm={() => {
						setShowSaveConfirm(false);
						onSaveAsDefault();
					}}
				/>
			)}
		</>
	);
};
